import React, { useState, useEffect, useMemo } from 'react';
import { 
  BookOpen, BarChart3, Clock, Target, Repeat, AlertTriangle,
  ChevronDown, ChevronUp, Sparkles, TrendingUp, Award, Zap,
  Eye, EyeOff, RefreshCw, AlertCircle, Type, Wand2, Check
} from 'lucide-react';
import { Button } from './ui/button';
import { Progress } from './ui/progress';
import { Badge } from './ui/badge';

// Common filter words that weaken prose
const FILTER_WORDS = [
  'very', 'really', 'just', 'actually', 'basically', 'literally',
  'definitely', 'certainly', 'probably', 'maybe', 'perhaps',
  'somewhat', 'rather', 'quite', 'simply', 'extremely',
  'absolutely', 'totally', 'completely', 'honestly', 'seriously'
];

// Common grammar mistakes to detect
const GRAMMAR_MISTAKES = [
  { wrong: 'your', correct: "you're", context: /your\s+(a|an|the|going|not|so|very|really|being|getting)\b/gi, hint: "your → you're (you are)" },
  { wrong: 'youre', correct: "you're", context: /\byoure\b/gi, hint: "youre → you're" },
  { wrong: 'its', correct: "it's", context: /its\s+(a|an|the|going|not|so|very|really|been|being)\b/gi, hint: "its → it's (it is)" },
  { wrong: 'their', correct: "they're", context: /their\s+(a|an|the|going|not|so|very|really|being)\b/gi, hint: "their → they're (they are)" },
  { wrong: 'theyre', correct: "they're", context: /\btheyre\b/gi, hint: "theyre → they're" },
  { wrong: 'theres', correct: "there's", context: /\btheres\b/gi, hint: "theres → there's" },
  { wrong: 'dont', correct: "don't", context: /\bdont\b/gi, hint: "dont → don't" },
  { wrong: 'cant', correct: "can't", context: /\bcant\b/gi, hint: "cant → can't" },
  { wrong: 'wont', correct: "won't", context: /\bwont\b/gi, hint: "wont → won't" },
  { wrong: 'didnt', correct: "didn't", context: /\bdidnt\b/gi, hint: "didnt → didn't" },
  { wrong: 'im', correct: "I'm", context: /\bim\b/gi, hint: "im → I'm" },
  { wrong: 'ive', correct: "I've", context: /\bive\b/gi, hint: "ive → I've" },
  { wrong: 'id', correct: "I'd", context: /\bid\s+(like|love|rather|prefer|say|think|be)\b/gi, hint: "id → I'd" },
  { wrong: 'alot', correct: "a lot", context: /\balot\b/gi, hint: "alot → a lot" },
  { wrong: 'could of', correct: "could have", context: /could\s+of\b/gi, hint: "could of → could have" },
  { wrong: 'would of', correct: "would have", context: /would\s+of\b/gi, hint: "would of → would have" },
  { wrong: 'should of', correct: "should have", context: /should\s+of\b/gi, hint: "should of → should have" },
];

// Calculate Flesch-Kincaid Reading Ease
const calculateReadability = (text) => {
  if (!text || text.trim().length === 0) return { score: 0, grade: 'N/A', level: 'Start writing...' };
  
  const sentences = text.split(/[.!?]+/).filter(s => s.trim().length > 0);
  const words = text.split(/\s+/).filter(w => w.length > 0);
  const syllables = words.reduce((count, word) => {
    return count + countSyllables(word);
  }, 0);
  
  if (sentences.length === 0 || words.length === 0) {
    return { score: 0, grade: 'N/A', level: 'Keep writing...' };
  }
  
  const avgSentenceLength = words.length / sentences.length;
  const avgSyllablesPerWord = syllables / words.length;
  
  // Flesch-Kincaid Reading Ease formula
  const score = 206.835 - (1.015 * avgSentenceLength) - (84.6 * avgSyllablesPerWord);
  const clampedScore = Math.max(0, Math.min(100, score));
  
  // Flesch-Kincaid Grade Level
  const gradeLevel = (0.39 * avgSentenceLength) + (11.8 * avgSyllablesPerWord) - 15.59;
  const grade = Math.max(1, Math.min(18, Math.round(gradeLevel)));
  
  let level;
  if (clampedScore >= 90) level = 'Very Easy (5th grade)';
  else if (clampedScore >= 80) level = 'Easy (6th grade)';
  else if (clampedScore >= 70) level = 'Fairly Easy (7th grade)';
  else if (clampedScore >= 60) level = 'Standard (8-9th grade)';
  else if (clampedScore >= 50) level = 'Fairly Difficult (10-12th)';
  else if (clampedScore >= 30) level = 'Difficult (College)';
  else level = 'Very Difficult (Graduate)';
  
  return { score: Math.round(clampedScore), grade, level };
};

// Count syllables in a word (approximation)
const countSyllables = (word) => {
  word = word.toLowerCase().replace(/[^a-z]/g, '');
  if (word.length <= 3) return 1;
  
  word = word.replace(/(?:[^laeiouy]es|ed|[^laeiouy]e)$/, '');
  word = word.replace(/^y/, '');
  
  const matches = word.match(/[aeiouy]{1,2}/g);
  return matches ? matches.length : 1;
};

// Calculate reading time
const calculateReadingTime = (wordCount) => {
  const wordsPerMinute = 200; // Average reading speed
  const minutes = Math.ceil(wordCount / wordsPerMinute);
  if (minutes < 1) return '< 1 min';
  if (minutes === 1) return '1 min';
  return `${minutes} mins`;
};

// Find repeated words
const findRepeatedWords = (text) => {
  if (!text) return [];
  
  const words = text.toLowerCase().split(/\s+/).filter(w => w.length > 3);
  const wordCount = {};
  
  words.forEach(word => {
    const cleaned = word.replace(/[^a-z]/g, '');
    if (cleaned.length > 3) {
      wordCount[cleaned] = (wordCount[cleaned] || 0) + 1;
    }
  });
  
  // Return words used more than 3 times, sorted by frequency
  return Object.entries(wordCount)
    .filter(([word, count]) => count > 3 && !['that', 'this', 'with', 'from', 'have', 'been', 'were', 'they', 'their', 'would', 'could', 'should', 'about', 'which', 'there', 'these', 'those', 'then', 'than', 'when', 'what', 'your', 'into'].includes(word))
    .sort((a, b) => b[1] - a[1])
    .slice(0, 8);
};

// Find filter words
const findFilterWords = (text) => {
  if (!text) return [];
  
  const words = text.toLowerCase().split(/\s+/);
  const found = {};
  
  words.forEach(word => {
    const cleaned = word.replace(/[^a-z]/g, '');
    if (FILTER_WORDS.includes(cleaned)) {
      found[cleaned] = (found[cleaned] || 0) + 1;
    }
  });
  
  return Object.entries(found).sort((a, b) => b[1] - a[1]);
};

// Find consecutive duplicate words (e.g., "woman woman")
const findConsecutiveDuplicates = (text) => {
  if (!text) return [];
  
  const words = text.split(/\s+/);
  const duplicates = [];
  
  for (let i = 0; i < words.length - 1; i++) {
    const word1 = words[i].toLowerCase().replace(/[^a-z]/g, '');
    const word2 = words[i + 1].toLowerCase().replace(/[^a-z]/g, '');
    
    if (word1.length > 1 && word1 === word2) {
      duplicates.push(`${words[i]} ${words[i + 1]}`);
    }
  }
  
  return duplicates;
};

// Find grammar mistakes
const findGrammarIssues = (text) => {
  if (!text) return [];
  
  const issues = [];
  
  GRAMMAR_MISTAKES.forEach(rule => {
    const matches = text.match(rule.context);
    if (matches) {
      matches.forEach(match => {
        if (!issues.find(i => i.hint === rule.hint)) {
          issues.push({ 
            match: match.trim(), 
            hint: rule.hint,
            type: 'grammar'
          });
        }
      });
    }
  });
  
  return issues;
};

// Shweebum Scribe Component
const ShweebumScribe = ({ 
  content = '', 
  wordCount = 0,
  dailyGoal = 500,
  onGoalChange,
  onContentChange, // New: callback to fix content
  compact = false 
}) => {
  const [isExpanded, setIsExpanded] = useState(!compact);
  const [showDetails, setShowDetails] = useState(false);
  const [localGoal, setLocalGoal] = useState(dailyGoal);
  
  // Load saved goal from localStorage
  useEffect(() => {
    const saved = localStorage.getItem('shweebum_scribe_goal');
    if (saved) {
      setLocalGoal(parseInt(saved, 10));
    }
  }, []);
  
  // Memoized calculations for performance
  const readability = useMemo(() => calculateReadability(content), [content]);
  const readingTime = useMemo(() => calculateReadingTime(wordCount), [wordCount]);
  const repeatedWords = useMemo(() => findRepeatedWords(content), [content]);
  const filterWords = useMemo(() => findFilterWords(content), [content]);
  const consecutiveDupes = useMemo(() => findConsecutiveDuplicates(content), [content]);
  const grammarIssues = useMemo(() => findGrammarIssues(content), [content]);
  
  // Check if there are any issues to report
  const hasIssues = repeatedWords.length > 0 || filterWords.length > 0 || consecutiveDupes.length > 0 || grammarIssues.length > 0;
  
  const goalProgress = Math.min(100, (wordCount / localGoal) * 100);
  const goalReached = wordCount >= localGoal;
  
  const handleGoalChange = (newGoal) => {
    setLocalGoal(newGoal);
    localStorage.setItem('shweebum_scribe_goal', newGoal.toString());
    onGoalChange?.(newGoal);
  };
  
  // Auto-fix grammar issue
  const fixGrammarIssue = (issue) => {
    if (!onContentChange) return;
    let newContent = content;
    
    // Apply the fix based on the pattern
    if (issue.hint.includes('im → I\'m')) {
      newContent = newContent.replace(/\bim\b/gi, "I'm");
    } else if (issue.hint.includes('your → you\'re')) {
      newContent = newContent.replace(/your\s+(a|an|the|going|not|so|very|really|being|getting)\b/gi, (match, after) => `you're ${after}`);
    } else if (issue.hint.includes('its → it\'s')) {
      newContent = newContent.replace(/its\s+(a|an|the|going|not|so|very|really|been|being)\b/gi, (match, after) => `it's ${after}`);
    } else if (issue.hint.includes('their → they\'re')) {
      newContent = newContent.replace(/their\s+(a|an|the|going|not|so|very|really|being)\b/gi, (match, after) => `they're ${after}`);
    } else if (issue.hint.includes('theyre → they\'re')) {
      newContent = newContent.replace(/\btheyre\b/gi, "they're");
    } else if (issue.hint.includes('theres → there\'s')) {
      newContent = newContent.replace(/\btheres\b/gi, "there's");
    } else if (issue.hint.includes('dont → don\'t')) {
      newContent = newContent.replace(/\bdont\b/gi, "don't");
    } else if (issue.hint.includes('cant → can\'t')) {
      newContent = newContent.replace(/\bcant\b/gi, "can't");
    } else if (issue.hint.includes('wont → won\'t')) {
      newContent = newContent.replace(/\bwont\b/gi, "won't");
    } else if (issue.hint.includes('didnt → didn\'t')) {
      newContent = newContent.replace(/\bdidnt\b/gi, "didn't");
    } else if (issue.hint.includes('ive → I\'ve')) {
      newContent = newContent.replace(/\bive\b/gi, "I've");
    } else if (issue.hint.includes('id → I\'d')) {
      newContent = newContent.replace(/\bid\s+(like|love|rather|prefer|say|think|be)\b/gi, (match, after) => `I'd ${after}`);
    } else if (issue.hint.includes('alot → a lot')) {
      newContent = newContent.replace(/\balot\b/gi, "a lot");
    } else if (issue.hint.includes('could of → could have')) {
      newContent = newContent.replace(/could\s+of\b/gi, "could have");
    } else if (issue.hint.includes('would of → would have')) {
      newContent = newContent.replace(/would\s+of\b/gi, "would have");
    } else if (issue.hint.includes('should of → should have')) {
      newContent = newContent.replace(/should\s+of\b/gi, "should have");
    } else if (issue.hint.includes('youre → you\'re')) {
      newContent = newContent.replace(/\byoure\b/gi, "you're");
    }
    
    onContentChange(newContent);
  };
  
  // Auto-fix duplicate word
  const fixDuplicate = (dupe) => {
    if (!onContentChange) return;
    // "woman woman" -> "woman"
    const words = dupe.split(' ');
    const pattern = new RegExp(`\\b${words[0]}\\s+${words[1]}\\b`, 'gi');
    const newContent = content.replace(pattern, words[0]);
    onContentChange(newContent);
  };
  
  // Fix all issues at once
  const fixAllIssues = () => {
    if (!onContentChange) return;
    let newContent = content;
    
    // Fix grammar issues
    grammarIssues.forEach(issue => {
      if (issue.hint.includes('im → I\'m')) {
        newContent = newContent.replace(/\bim\b/gi, "I'm");
      } else if (issue.hint.includes('your → you\'re')) {
        newContent = newContent.replace(/your\s+(a|an|the|going|not|so|very|really|being|getting)\b/gi, (match, after) => `you're ${after}`);
      } else if (issue.hint.includes('its → it\'s')) {
        newContent = newContent.replace(/its\s+(a|an|the|going|not|so|very|really|been|being)\b/gi, (match, after) => `it's ${after}`);
      } else if (issue.hint.includes('their → they\'re')) {
        newContent = newContent.replace(/their\s+(a|an|the|going|not|so|very|really|being)\b/gi, (match, after) => `they're ${after}`);
      } else if (issue.hint.includes('theyre → they\'re')) {
        newContent = newContent.replace(/\btheyre\b/gi, "they're");
      } else if (issue.hint.includes('theres → there\'s')) {
        newContent = newContent.replace(/\btheres\b/gi, "there's");
      } else if (issue.hint.includes('dont → don\'t')) {
        newContent = newContent.replace(/\bdont\b/gi, "don't");
      } else if (issue.hint.includes('cant → can\'t')) {
        newContent = newContent.replace(/\bcant\b/gi, "can't");
      } else if (issue.hint.includes('wont → won\'t')) {
        newContent = newContent.replace(/\bwont\b/gi, "won't");
      } else if (issue.hint.includes('didnt → didn\'t')) {
        newContent = newContent.replace(/\bdidnt\b/gi, "didn't");
      } else if (issue.hint.includes('ive → I\'ve')) {
        newContent = newContent.replace(/\bive\b/gi, "I've");
      } else if (issue.hint.includes('alot → a lot')) {
        newContent = newContent.replace(/\balot\b/gi, "a lot");
      } else if (issue.hint.includes('could of → could have')) {
        newContent = newContent.replace(/could\s+of\b/gi, "could have");
      } else if (issue.hint.includes('would of → would have')) {
        newContent = newContent.replace(/would\s+of\b/gi, "would have");
      } else if (issue.hint.includes('should of → should have')) {
        newContent = newContent.replace(/should\s+of\b/gi, "should have");
      } else if (issue.hint.includes('youre → you\'re')) {
        newContent = newContent.replace(/\byoure\b/gi, "you're");
      }
    });
    
    // Fix consecutive duplicates
    consecutiveDupes.forEach(dupe => {
      const words = dupe.split(' ');
      const pattern = new RegExp(`\\b${words[0]}\\s+${words[1]}\\b`, 'gi');
      newContent = newContent.replace(pattern, words[0]);
    });
    
    onContentChange(newContent);
  };
  
  // Sentence count
  const sentenceCount = content ? content.split(/[.!?]+/).filter(s => s.trim().length > 0).length : 0;
  const avgSentenceLength = sentenceCount > 0 ? Math.round(wordCount / sentenceCount) : 0;
  
  if (!isExpanded) {
    // Collapsed mini view
    return (
      <button
        onClick={() => setIsExpanded(true)}
        className="fixed bottom-4 right-4 z-40 flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-emerald-600 to-teal-600 text-white shadow-lg hover:shadow-emerald-500/30 transition-all hover:scale-105"
      >
        <Sparkles className="w-4 h-4" />
        <span className="text-sm font-medium">Scribe</span>
        <Badge className="bg-white/20 text-white text-xs">{wordCount}</Badge>
      </button>
    );
  }
  
  return (
    <div className="bg-slate-900/95 backdrop-blur border border-emerald-500/30 rounded-xl shadow-xl overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between p-3 bg-gradient-to-r from-emerald-900/50 to-teal-900/50 border-b border-emerald-500/20">
        <div className="flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-emerald-400" />
          <span className="font-semibold text-emerald-300 text-sm">Shweebum Scribe</span>
          <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30 text-[10px]">FREE</Badge>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setIsExpanded(false)}
          className="h-6 w-6 p-0 text-slate-400 hover:text-white"
        >
          <EyeOff className="w-4 h-4" />
        </Button>
      </div>
      
      <div className="p-4 space-y-4">
        {/* Quick Stats Row */}
        <div className="grid grid-cols-3 gap-2">
          <div className="text-center p-2 rounded-lg bg-slate-800/50">
            <div className="text-lg font-bold text-white">{wordCount.toLocaleString()}</div>
            <div className="text-[10px] text-slate-400 uppercase">Words</div>
          </div>
          <div className="text-center p-2 rounded-lg bg-slate-800/50">
            <div className="text-lg font-bold text-cyan-400">{readingTime}</div>
            <div className="text-[10px] text-slate-400 uppercase">Read Time</div>
          </div>
          <div className="text-center p-2 rounded-lg bg-slate-800/50">
            <div className="text-lg font-bold text-violet-400">{readability.score}</div>
            <div className="text-[10px] text-slate-400 uppercase">Readability</div>
          </div>
        </div>
        
        {/* Daily Goal Progress */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Target className="w-4 h-4 text-amber-400" />
              <span className="text-sm text-slate-300">Daily Goal</span>
            </div>
            <div className="flex items-center gap-2">
              {goalReached && (
                <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/30 text-[10px]">
                  <Award className="w-3 h-3 mr-1" /> Reached!
                </Badge>
              )}
              <select
                value={localGoal}
                onChange={(e) => handleGoalChange(parseInt(e.target.value, 10))}
                className="bg-slate-800 border border-slate-700 rounded px-2 py-1 text-xs text-slate-300"
              >
                <option value={250}>250 words</option>
                <option value={500}>500 words</option>
                <option value={1000}>1,000 words</option>
                <option value={2000}>2,000 words</option>
                <option value={5000}>5,000 words</option>
              </select>
            </div>
          </div>
          <div className="relative">
            <Progress value={goalProgress} className="h-3 bg-slate-800" />
            <div className="absolute inset-0 flex items-center justify-center">
              <span className="text-[10px] font-medium text-white drop-shadow">
                {wordCount} / {localGoal.toLocaleString()}
              </span>
            </div>
          </div>
        </div>
        
        {/* Readability */}
        <div className="p-3 rounded-lg bg-slate-800/50 border border-slate-700/50">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <BookOpen className="w-4 h-4 text-violet-400" />
              <span className="text-sm font-medium text-white">Readability</span>
            </div>
            <Badge className={`text-[10px] ${
              readability.score >= 60 ? 'bg-emerald-500/20 text-emerald-400' : 
              readability.score >= 40 ? 'bg-amber-500/20 text-amber-400' : 
              'bg-red-500/20 text-red-400'
            }`}>
              Grade {readability.grade}
            </Badge>
          </div>
          <p className="text-xs text-slate-400">{readability.level}</p>
          <div className="mt-2 flex items-center gap-4 text-xs text-slate-500">
            <span>{sentenceCount} sentences</span>
            <span>~{avgSentenceLength} words/sentence</span>
          </div>
        </div>
        
        {/* Quick Issues Alert - Shows when issues detected */}
        {hasIssues && !showDetails && (
          <button
            onClick={() => setShowDetails(true)}
            className="w-full p-2 rounded-lg bg-rose-950/40 border border-rose-500/30 flex items-center justify-center gap-2 hover:bg-rose-950/60 transition-colors"
          >
            <AlertCircle className="w-4 h-4 text-rose-400" />
            <span className="text-xs text-rose-300 font-medium">
              {grammarIssues.length + consecutiveDupes.length} issue{grammarIssues.length + consecutiveDupes.length !== 1 ? 's' : ''} found - tap to review
            </span>
          </button>
        )}
        
        {/* Toggle Details */}
        <button
          onClick={() => setShowDetails(!showDetails)}
          className="w-full flex items-center justify-center gap-1 text-xs text-slate-400 hover:text-slate-300 transition-colors"
        >
          {showDetails ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
          {showDetails ? 'Hide Details' : (hasIssues ? `Show Issues (${grammarIssues.length + consecutiveDupes.length + repeatedWords.length + filterWords.length})` : 'Show Word Analysis')}
        </button>
        
        {showDetails && (
          <>
            {/* Fix All Button */}
            {(grammarIssues.length > 0 || consecutiveDupes.length > 0) && onContentChange && (
              <button
                onClick={fixAllIssues}
                className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white text-sm font-medium transition-colors"
              >
                <Wand2 className="w-4 h-4" />
                Fix All Issues ({grammarIssues.length + consecutiveDupes.length})
              </button>
            )}
            
            {/* Grammar Issues - Most Important */}
            {grammarIssues.length > 0 && (
              <div className="p-3 rounded-lg bg-rose-950/40 border border-rose-500/30">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <AlertCircle className="w-4 h-4 text-rose-400" />
                    <span className="text-sm font-medium text-rose-300">Grammar Check</span>
                  </div>
                </div>
                <div className="space-y-1.5">
                  {grammarIssues.map((issue, idx) => (
                    <div 
                      key={idx} 
                      className="flex items-center justify-between px-2 py-1.5 rounded bg-rose-500/15 text-xs"
                    >
                      <div>
                        <span className="text-rose-300">"{issue.match}"</span>
                        <span className="text-rose-400 ml-2">→ {issue.hint}</span>
                      </div>
                      {onContentChange && (
                        <button
                          onClick={() => fixGrammarIssue(issue)}
                          className="ml-2 px-2 py-1 rounded bg-emerald-600 hover:bg-emerald-500 text-white text-[10px] font-medium flex items-center gap-1"
                        >
                          <Wand2 className="w-3 h-3" /> Fix
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {/* Consecutive Duplicates */}
            {consecutiveDupes.length > 0 && (
              <div className="p-3 rounded-lg bg-orange-950/40 border border-orange-500/30">
                <div className="flex items-center gap-2 mb-2">
                  <Type className="w-4 h-4 text-orange-400" />
                  <span className="text-sm font-medium text-orange-300">Duplicate Words</span>
                </div>
                <div className="space-y-1.5">
                  {consecutiveDupes.map((dupe, idx) => (
                    <div 
                      key={idx} 
                      className="flex items-center justify-between px-2 py-1 rounded bg-orange-500/20 text-xs"
                    >
                      <span className="text-orange-300">"{dupe}"</span>
                      {onContentChange && (
                        <button
                          onClick={() => fixDuplicate(dupe)}
                          className="ml-2 px-2 py-1 rounded bg-emerald-600 hover:bg-emerald-500 text-white text-[10px] font-medium flex items-center gap-1"
                        >
                          <Wand2 className="w-3 h-3" /> Fix
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {/* Repeated Words */}
            {repeatedWords.length > 0 && (
              <div className="p-3 rounded-lg bg-amber-950/30 border border-amber-500/20">
                <div className="flex items-center gap-2 mb-2">
                  <Repeat className="w-4 h-4 text-amber-400" />
                  <span className="text-sm font-medium text-amber-300">Repeated Words</span>
                </div>
                <div className="flex flex-wrap gap-1">
                  {repeatedWords.map(([word, count]) => (
                    <span 
                      key={word} 
                      className="px-2 py-0.5 rounded bg-amber-500/20 text-amber-300 text-xs"
                    >
                      {word} <span className="text-amber-500">×{count}</span>
                    </span>
                  ))}
                </div>
              </div>
            )}
            
            {/* Filter Words */}
            {filterWords.length > 0 && (
              <div className="p-3 rounded-lg bg-red-950/30 border border-red-500/20">
                <div className="flex items-center gap-2 mb-2">
                  <AlertTriangle className="w-4 h-4 text-red-400" />
                  <span className="text-sm font-medium text-red-300">Filter Words</span>
                  <span className="text-[10px] text-red-400/70">(weaken prose)</span>
                </div>
                <div className="flex flex-wrap gap-1">
                  {filterWords.map(([word, count]) => (
                    <span 
                      key={word} 
                      className="px-2 py-0.5 rounded bg-red-500/20 text-red-300 text-xs"
                    >
                      {word} <span className="text-red-500">×{count}</span>
                    </span>
                  ))}
                </div>
                <p className="text-[10px] text-red-400/60 mt-2">
                  Consider removing or replacing these words
                </p>
              </div>
            )}
            
            {!hasIssues && (
              <div className="p-3 rounded-lg bg-emerald-950/30 border border-emerald-500/20 text-center">
                <Zap className="w-5 h-5 text-emerald-400 mx-auto mb-1" />
                <p className="text-sm text-emerald-300">Looking good!</p>
                <p className="text-xs text-emerald-400/60">No issues detected</p>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default ShweebumScribe;
